import {
  Component,
  signal,
  computed,
  input,
  OnInit,
  inject,
  PLATFORM_ID,
  ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { environment } from '@awo-care/shared-auth';

interface InformationStep {
  icon: string;
  title: string;
  description: string;
  category: string;
}

@Component({
  selector: 'app-how-it-works',
  imports: [MatCardModule, MatIconModule, RouterLink],
  template: `
    <div class="container">
      <div class="header">
        @if (headingLevel() === 1) {
          <h1 id="how-it-works-heading" i18n="@@home.informationSteps.header">
            Anmeldung zur Schülerbetreuung
          </h1>
        } @else {
          <h2 id="how-it-works-heading" i18n="@@home.informationSteps.header">
            Anmeldung zur Schülerbetreuung
          </h2>
        }
        <p i18n="@@home.informationSteps.subtitle">
          Folgen Sie unserem einfachen Schritt-für-Schritt-Prozess, um Ihr Kind
          für die Schülerbetreuung anzumelden.
        </p>
      </div>

      <!-- Progress bar removed for neutral display -->

      <div class="steps-grid">
        @for (step of steps(); track step.title; let i = $index) {
        <mat-card
          class="step-card"
          [class.animated]="isAnimated()"
          [style.animation-delay]="i * 100 + 'ms'"
        >
          <div class="step-header">
            <div class="step-icon">
              <mat-icon aria-hidden="true">{{ step.icon }}</mat-icon>
            </div>
            <div class="step-info">
              <div class="step-number">
                <span i18n="@@home.informationSteps.stepLabel">Schritt</span>
                <span>{{ i + 1 }}</span>
              </div>
              <!--
                Die Karten-Titel lagen bisher auf h2 — also auf derselben Ebene
                wie die Abschnitts-Ueberschrift (WCAG 1.3.1). role/aria-level
                statt @if-Kaskade, weil der Wert an headingLevel haengt.
              -->
              <div
                class="step-title"
                role="heading"
                [attr.aria-level]="headingLevel() + 1"
              >
                {{ step.title }}
              </div>
            </div>
          </div>

          <p class="step-description">{{ step.description }}</p>

          <div class="step-category">
            <span>{{ step.category }}</span>
          </div>
        </mat-card>
        }
      </div>

      @if (!isProduction) {
      <div class="faq-hint">
        <p i18n="@@home.informationSteps.faqHint">
          Haben Sie Fragen zur Stornierung oder Kündigung?
          <a routerLink="/faq">Schauen Sie in unsere häufigen Fragen.</a>
        </p>
      </div>
      }
    </div>
    <!-- <lib-logo-footer class="with-spacing" /> -->
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        padding: 2rem 1rem;
      }

      .container {
        max-width: 1200px;
        width: 100%;
        margin: 0 auto;
      }

      .header {
        text-align: center;
        margin-bottom: 1rem;
        animation: fadeInDown 0.8s ease-out;
      }

      .header h2 {
        font-size: 2.2rem;
        font-weight: 500;
        color: #333;
        margin: 0 0 1rem 0;
        letter-spacing: -0.01em;

        @media (max-width: 768px) {
          font-size: 1.8rem;
        }
      }

      .header p {
        font-size: 1.1rem;
        color: var(--awo-text-muted, #6f6f6f);
        margin: 0;
        max-width: 600px;
        margin: 0 auto;
        line-height: 1.5;
        text-align: center;
        text-wrap: balance;
        font-weight: 400;
        @media (max-width: 768px) {
          font-size: 1rem;
        }
      }

      .steps-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
        gap: 1rem;
        margin-bottom: 0.5rem;
      }

      .step-card {
        background: white;
        border-radius: 8px;
        padding: 1rem;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        transition: all 0.3s ease;
        border: 1px solid var(--theme-border);
        opacity: 0;
        transform: translateY(30px);
        display: flex;
        flex-direction: column;
        height: 100%;
        position: relative;
        overflow: hidden;
      }

      .step-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: var(--theme-primary);
        transform: scaleX(0);
        transform-origin: left;
        transition: transform 0.4s ease;
      }

      .step-card:hover::before {
        transform: scaleX(1);
      }

      .step-card.animated {
        animation: slideUp 0.6s ease-out both;
      }

      .step-card:hover {
        transform: translateY(-8px) scale(1.02);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
      }

      .step-card:hover .step-icon {
        transform: scale(1.1);
      }

      .step-header {
        display: flex;
        align-items: center;
        margin-bottom: 1rem;
      }

      .step-icon {
        width: 60px;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;
        transition: all 0.3s ease;
      }

      .step-icon mat-icon {
        color: var(--theme-primary);
        font-size: 28px;
        width: 28px;
        height: 28px;
      }

      .step-info {
        flex: 1;
      }

      .step-number {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--theme-primary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.25rem;
      }

      .step-title {
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--theme-regularTextColor);
        margin: 0;
        line-height: 1.3;
      }

      .step-description {
        color: var(--theme-regularTextColor);
        line-height: 1.6;
        font-size: 1rem;
        margin-bottom: 1rem;
        opacity: 0.8;
        flex-grow: 1;
      }

      .step-category {
        display: inline-block;
      }

      .step-category span {
        display: inline-flex;
        align-items: center;
        padding: 0.375rem 0.875rem;
        border-radius: 16px;
        font-size: 0.8125rem;
        font-weight: 400;
        letter-spacing: 0.5px;
        border: 1px solid var(--theme-primary);
        color: var(--theme-primary);
        transition: all 0.3s ease;
      }

      .faq-hint {
        text-align: center;
        margin-top: 2rem;
        padding: 1.25rem 1.5rem;
        background: var(--theme-info-surface);
        border-radius: 8px;
        border: 1px solid var(--theme-info-border);
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
      }

      .faq-hint p {
        margin: 0;
        font-size: 0.95rem;
        line-height: 1.5;
        color: var(--theme-regularTextColor);
      }

      .faq-hint a {
        color: var(--theme-accent-text, #08acd3);
        font-weight: 500;
        text-decoration: underline;
        white-space: nowrap;
      }

      @keyframes fadeInDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (max-width: 768px) {
        :host {
          padding: 1rem 0.5rem;
        }

        .steps-grid {
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }

        .step-card {
          padding: 1.5rem;
        }

        .step-header {
          flex-direction: column;
          text-align: center;
          align-items: center;
        }

        .step-icon {
          margin-right: 0;
          margin-bottom: 1rem;
        }

        .step-info {
          text-align: center;
        }
      }

      @media (max-width: 450px) {
        .steps-grid {
          grid-template-columns: 1fr;
        }

        .step-card {
          margin: 0 0.5rem;
        }
      }
    `,
  ],
})
export class HowItWorksComponent implements OnInit {
  /**
   * Ebene der Abschnitts-Ueberschrift. Default 2, weil die Komponente in
   * customer-ui und auf der Website-Startseite als Abschnitt unter einer
   * bestehenden h1 haengt. Als eigenstaendige Route (Website `/info`) ist sie
   * der Seiteninhalt und braucht 1, sonst startet die Seite ohne h1.
   */
  readonly headingLevel = input<1 | 2>(2);

  private platformId = inject(PLATFORM_ID);
  private animated = signal(false);
  readonly isProduction = environment.production;

  readonly isAnimated = computed(() => this.animated());

  // Static steps definition - no longer depends on student state
  readonly steps = computed<InformationStep[]>(() => [
    {
      icon: 'person_add',
      title: $localize`:@@home.informationSteps.step1Title:Konto erstellen`,
      description: $localize`:@@home.informationSteps.step1Description:Erstellen Sie ein Konto um den gesamten Anmeldeprozess Ihres Kindes zu verwalten und über wichtige Benachrichtigungen informiert zu bleiben.`,
      category: $localize`:@@home.informationSteps.category.account:Konto`,
    },
    {
      icon: 'school',
      title: $localize`:@@home.informationSteps.step2Title:Kind anmelden`,
      description: $localize`:@@home.informationSteps.step2Description:Melden Sie Ihr Kind an der gewünschten Schule zum Betreuungsangebot an. Sie erhalten direkt nach der Anmeldung eine Bestätigungsemail.`,
      category: $localize`:@@home.informationSteps.category.registration:Anmeldung`,
    },
    {
      icon: 'verified',
      title: $localize`:@@home.informationSteps.step3Title:Genehmigung erhalten`,
      description: $localize`:@@home.informationSteps.step3Description:Nach Ablauf der Anmeldefrist erhalten Sie eine Zu- oder Absage für den gewünschten Betreuungsplatz.`,
      category: $localize`:@@home.informationSteps.category.approval:Genehmigung`,
    },
    {
      icon: 'description',
      title: $localize`:@@home.informationSteps.step4Title:Vertrag erhalten`,
      description: $localize`:@@home.informationSteps.step4Description:Nach Eingang der Zusage sind Sie für den Abschluss eines Betreuungsvertrags freigeschaltet. Loggen Sie sich in Ihre AWO Care Konto ein und beginnen mit dem Abschluss des Betreuungsvertrags.`,
      category: $localize`:@@home.informationSteps.category.contract:Vertrag`,
    },
    {
      icon: 'payments',
      title: $localize`:@@home.informationSteps.step5Title:Informationen ausfüllen`,
      description: $localize`:@@home.informationSteps.step5Description:Nach der Eingabe aller notwendigen Daten, bitten wir Sie um die Erteilung eines SEPA-Lastschriftmandates zur Abrechnung der Betreuungsbeiträge. Sie können hier zwischen einer monatlichen oder jährlichen Zahlungsweise wählen.`,
      category: $localize`:@@home.informationSteps.category.payment:Zahlung`,
    },
    {
      icon: 'event_available',
      title: $localize`:@@home.informationSteps.step6Title:Informationen zum ersten Schultag`,
      description: $localize`:@@home.informationSteps.step6Description:Nach Abschluss dieses Vorgangs erhalten Sie eine Vertragsbestätigung per E-Mail sowie weitere Informationen zum ersten Betreuungstag Ihres Kindes.`,
      category: $localize`:@@home.informationSteps.category.start:Start`,
    },
  ]);

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Trigger animations after a short delay
      setTimeout(() => {
        this.animated.set(true);
      }, 200);
    }
  }
}
